import React, { useState } from 'react'
import moment from 'moment'
import { Tabs, Drawer, Dropdown, Menu, Typography, DatePicker } from 'antd'
import { useParams, Link, useHistory, Switch, Route } from 'react-router-dom'
import Creatable from 'react-select/creatable'
import { useSelector } from 'react-redux'

import { AiOutlineDown } from 'react-icons/ai'

import { usePet, useToggle, notifOps, stringifyDate, useListData, stringifyTime } from '../../modules'

import Receive from '../reusable/receive'
import images from '../../images'

import Invoice from './invoice'
import NewInvoice from './newInvoice'
import RecordPayment from './recordPayment'
import RecordCreditNote from './recordCreditNote'
import CreditNoteDetail from './creditNoteDetail'
import ApplyCreditNote from './applyCreditNote'

const { TabPane } = Tabs
const { Paragraph } = Typography
const { RangePicker } = DatePicker

const Statement = ({ id, path }) => {
    const [string, setString] = useState('')
    const [ranges, setRanges] = useState()
    
    const [
        { loading, error, fetch },
        { list: statement },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('statementBySubject', ranges ?
        { string, subject: id, from: ranges[0]?._d ?? null, to: ranges[1]?._d ?? null }
        : { string, subject: id }
        , { notify: true, manual: false }, 'entries')

    const refresh = () => {
        navigate(1)
        fetch()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    return (
        <>
            <div className="col-span-full xl:col-span-8 bg-white">
                <div className="sm:flex sm:justify-between sm:items-center mb-8">
                    <input
                        onChange={e => search(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Search using transaction ref" />
                    <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                        <RangePicker
                            onChange={selectRange}
                            ranges={{
                                'Today': [moment().startOf('day'), moment().endOf('day')],
                                'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
                                'This Week': [moment().startOf('week'), moment().endOf('week')],
                                'This Month': [moment().startOf('month'), moment().endOf('month')],
                            }} />
                    </div>
                </div>
                <Receive
                    error={error}
                    loading={loading}
                    progress>
                    {
                        statement.length > 0 ?
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Date</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Details</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Debit</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Credit</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Balance</div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        statement.map((stat, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">
                                                        { ((page - 1) * limit) + (key + 1)}
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{ stringifyDate(stat.date, true) }</div>
                                                    <p className="mb-0 text-gray-400 text-xs italic">{ stringifyTime(stat.date) }</p>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{stat?.type}</div>
                                                    <p className="mb-0 text-gray-400 text-xs italic">{ stat?.notes }</p>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{ stat?.amount < 0 ? stat.amount : 0 }</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{ stat?.amount > 0 ? stat.amount : 0 }</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-left">{ stat?.newBalance }</div>
                                                </td>
                                                <td className="p-2 text-center">
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No entries were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const Transactions = ({ id, path }) => {
    const [string, setString] = useState('')
    const [ranges, setRanges] = useState()
    
    const [
        { loading, error, fetch },
        { list: transactions },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('transactions', ranges ?
        { string, subject: id, from: ranges[0]?._d ?? null, to: ranges[1]?._d ?? null }
        : { string, subject: id }
        , { notify: true, manual: false })

    const refresh = () => {
        navigate(1)
        fetch()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    return (
        <>
            <div className="col-span-full xl:col-span-8 bg-white">
                <div className="sm:flex sm:justify-between sm:items-center mb-8">
                    <input
                        onChange={e => search(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Search using transaction ref" />
                    <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                        <RangePicker
                            onChange={selectRange}
                            ranges={{
                                'Today': [moment().startOf('day'), moment().endOf('day')],
                                'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
                                'This Week': [moment().startOf('week'), moment().endOf('week')],
                                'This Month': [moment().startOf('month'), moment().endOf('month')],
                            }} />
                    </div>
                </div>
                <Receive
                    error={error}
                    loading={loading}
                    progress>
                    {
                        transactions.length > 0 ?
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Amount</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Details</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Description</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Recorded By</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Date</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center"></div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        transactions.map((transaction, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">
                                                        { ((page - 1) * limit) + (key + 1)}
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">Ksh. {transaction?.amount}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{transaction?.method}</div>
                                                    { transaction?.ref && <p className="text-gray-700 text-xs italic mb-0">#{ transaction?.ref }</p> }
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{transaction?.description}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{transaction?.recordedBy?.name}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-left">{stringifyDate(transaction.date)}</div>
                                                </td>
                                                <td className="p-2 text-center">
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No transactions were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const PurchaseOrders = ({ id, path }) => {
    const [string, setString] = useState('')
    const [ranges, setRanges] = useState()
    
    const [
        { loading, error, fetch },
        { list: purchaseOrders },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('purchaseOrders', ranges ?
        { string, supplier: id, from: ranges[0]?._d ?? null, to: ranges[1]?._d ?? null }
        : { string, supplier: id }
        , { notify: true, manual: false })

    const refresh = () => {
        navigate(1)
        fetch()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    return (
        <>
            <div className="col-span-full xl:col-span-8 bg-white">
                <div className="sm:flex sm:justify-between sm:items-center mb-8">
                    <input
                        onChange={e => search(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Search using LPO code" />
                    <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                        <RangePicker
                            onChange={selectRange}
                            ranges={{
                                'Today': [moment().startOf('day'), moment().endOf('day')],
                                'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
                                'This Week': [moment().startOf('week'), moment().endOf('week')],
                                'This Month': [moment().startOf('month'), moment().endOf('month')],
                            }} />
                    </div>
                </div>
                <Receive
                    error={error}
                    loading={loading}
                    progress>
                    {
                        purchaseOrders.length > 0 ?
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Code</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Status</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Date</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center"></div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        purchaseOrders.map((po, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">{((page - 1) * limit) + key + 1}.</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{po?.code}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">{po?.approved ? 'Approved' : 'Approval Pending'}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">{stringifyDate(po.date)}</div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Dropdown
                                                        overlay={
                                                            <Menu>
                                                                <Link to={`${path}/purchaseOrders/${po._id}`}>
                                                                    <Menu.Item>View</Menu.Item>
                                                                </Link>
                                                                {
                                                                    !po?.committed &&
                                                                    <>
                                                                        <Menu.Item>Commit Purchase Orders</Menu.Item>
                                                                        <Menu.Item>Delete Purchase Orders</Menu.Item>
                                                                    </>
                                                                }
                                                            </Menu>
                                                        }>
                                                        <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No purchase orders were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const CreditNotes = ({ id, path }) => {
    const [string, setString] = useState('')

    const [
        { loading, error, fetch },
        { list: creditNotes },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('creditNotes', { supplier: id }, { notify: true, manual: false }, 'creditNotes')

    const refresh = () => {
        navigate(1)
        fetch()
    }

    return (
        <div className="col-span-full xl:col-span-8 bg-white">
            <Receive
                error={error}
                loading={loading}
                progress>
                {
                    creditNotes.length > 0 ?
                        <table className="table-auto w-full">
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                    <th className="p-2"><div className="font-semibold text-left">Type</div></th>
                                    <th className="p-2"><div className="font-semibold text-left">Invoice</div></th>
                                    <th className="p-2"><div className="font-semibold text-left">Reason</div></th>
                                    <th className="p-2"><div className="font-semibold text-center">Amount</div></th>
                                    <th className="p-2"><div className="font-semibold text-center">Applied</div></th>
                                    <th className="p-2"><div className="font-semibold text-center">Unused</div></th>
                                    <th className="p-2"><div className="font-semibold text-left">Date</div></th>
                                    <th className="p-2"><div className="font-semibold text-center"></div></th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {
                                    creditNotes.map((note, key) => {
                                        const unused = note.amount - note.amountApplied
                                        return (
                                            <tr key={ note._id }>
                                                <td className="p-2"><div className="text-center">{((page - 1) * limit) + key + 1}.</div></td>
                                                <td className="p-2"><div className="capitalize">{ note.type }</div></td>
                                                <td className="p-2"><div>{ note.invoice?.code ?? '—' }</div></td>
                                                <td className="p-2"><div>{ note.reason }</div></td>
                                                <td className="p-2"><div className="text-center">Ksh. { note.amount }</div></td>
                                                <td className="p-2"><div className="text-center">Ksh. { note.amountApplied }</div></td>
                                                <td className="p-2"><div className="text-center">Ksh. { unused }</div></td>
                                                <td className="p-2"><div>{ stringifyDate(note.date) }</div></td>
                                                <td className="p-2 text-center">
                                                    <Dropdown
                                                        overlay={
                                                            <Menu>
                                                                <Link to={`${path}/${id}/creditNotes/${note._id}`}>
                                                                    <Menu.Item>View</Menu.Item>
                                                                </Link>
                                                                {
                                                                    unused > 0 &&
                                                                    <Link to={`${path}/${id}/creditNotes/${note._id}/apply/${id}`}>
                                                                        <Menu.Item>Apply</Menu.Item>
                                                                    </Link>
                                                                }
                                                            </Menu>
                                                        }>
                                                        <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </table>
                        :
                        <div className="w-full h-16 flex items-center justify-center">
                            <p className="font-semibold text-base">No credit notes were found</p>
                        </div>
                }
            </Receive>
            {Paginate}
            <Switch>
                <Route path={ `${path}/${id}/creditNotes/:noteId/apply/:supplierId` }>
                    <ApplyCreditNote onPerform={ refresh } />
                </Route>
                <Route path={ `${path}/${id}/creditNotes/:noteId` }>
                    <CreditNoteDetail />
                </Route>
            </Switch>
        </div>
    )
}

const Invoices = ({ select, onComplete: onPerform, selected, setInvoices, supplier, id, path }) => {
    const [string, setString] = useState('')
    const [ranges, setRanges] = useState()
    
    const [
        { loading: invLoading, error: invError, fetch },
        { list: invoices },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('invoices', ranges ?
        { string, supplier: id, from: ranges[0]?._d ?? null, to: ranges[1]?._d ?? null }
        : { string, supplier: id }
        , { notify: true, manual: false })

    const refresh = () => {
        navigate(1)
        fetch()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    const choose = inv => setInvoices(invs => [...invs, inv])

    const onComplete = () => {
        onPerform()
        refresh()   
    }

    return (
        <>
            <div className="col-span-full xl:col-span-8 bg-white">
                <div className="sm:flex sm:justify-between sm:items-center mb-8">
                    <input
                        onChange={e => search(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Search using invoice code" />
                    <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                        <RangePicker
                            onChange={selectRange}
                            ranges={{
                                'Today': [moment().startOf('day'), moment().endOf('day')],
                                'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
                                'This Week': [moment().startOf('week'), moment().endOf('week')],
                                'This Month': [moment().startOf('month'), moment().endOf('month')],
                            }} />
                    </div>
                </div>
                <Receive
                    error={invError}
                    loading={invLoading}
                    progress>
                    {
                        invoices.length > 0 ?
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Code</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Status</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Date</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center"></div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        invoices.map((invoice, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">
                                                        {
                                                            select && !invoice?.paid && invoice.committed ?
                                                                <input
                                                                    onChange={ () => choose(invoice) }
                                                                    checked={ selected.some(inv => invoice._id === inv._id) }
                                                                    type="checkbox"
                                                                    className="form-checkbox cursor-pointer h-5 w-5" />
                                                            : `${((page - 1) * limit) + (key + 1)}.`
                                                        }
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{invoice?.code}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">{ invoice.status }</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">{stringifyDate(invoice.date)}</div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Dropdown
                                                        overlay={
                                                            <Menu>
                                                                <Link to={`${path}/${id}/invoices/${invoice._id}`}>
                                                                    <Menu.Item>View</Menu.Item>
                                                                </Link>
                                                                {
                                                                    !invoice?.committed &&
                                                                    <>
                                                                        <Menu.Item>Commit Invoice</Menu.Item>
                                                                        <Menu.Item>Delete Invoice</Menu.Item>
                                                                    </>
                                                                }
                                                            </Menu>
                                                        }>
                                                        <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No invoices were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
            <Switch>
                <Route path={ `${path}/${id}/invoices/new` }>
                    <NewInvoice supplier={ supplier } path={ `${path}/${id}` } onClose={ refresh } />
                </Route>
                <Route path={ `${path}/${id}/recordPayment` }>
                    <RecordPayment invoices={ selected } supplier={ supplier } path={ `${path}/${id}` } onPerform={ onComplete } />
                </Route>
                <Route path={ `${path}/${id}/recordCreditNote` }>
                    <RecordCreditNote invoices={ selected } supplier={ supplier } path={ `${path}/${id}` } onPerform={ onComplete } />
                </Route>
                <Route path={ `${path}/${id}/invoices/:id` }>
                    <Invoice path={ `${path}/${id}` } />
                </Route>
            </Switch>
        </>
    )
}

const Supplier = ({ path, onPerform }) => {
    const [supplier, setSupplier] = useState()
    const [field, setField] = useState()
    const [val, setVal] = useState()
    const [vals, setVals] = useState([])
    const [emails, setEmails] = useState([])
    const [invoices, setInvoices] = useState([])
    const [phones, setPhones] = useState([])

    const [visible, toggleVisible] = useToggle(true)
    const [select, toggleSelect] = useToggle()

    const { user } = useSelector(state => state.application)

    const history = useHistory()

    const { sid } = useParams()

    const togSelect = () => {
        setInvoices([])
        toggleSelect()
    }

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 100)
    }

    const [{ loading, error }, getSupplier] = usePet('supplier', { id: sid }, {
        notify: true, callback: ({ data }) => {
            setEmails(data.emails.map(email => ({ label: email, value: email })) ?? [])
            setPhones(data.phones.map(phone => ({ label: phone, value: phone })) ?? [])
            setSupplier(data)
        }
    })

    const canPay = invoices?.length > 0 && invoices?.every(invoice => !invoice.paid && invoice.committed)
    // No !invoice.paid restriction - a credit note (e.g. a supplier-side price correction
    // discovered after the fact) can apply against an already-paid invoice too.
    const canCredit = invoices?.length > 0 && invoices?.every(invoice => invoice.committed)

    const onComplete = (_, notif, ops) => {
        onPerform()
        getSupplier()
        setField()
        setVal()

        notif('Changes have been saved.', ops('success'))
    }

    const [{ loading: eLoading }, editSupplier] = usePet('editSupplierDetails', {
        id: sid,
        field,
        val,
        vals: vals ? vals.map(vl => vl.label) : undefined
    }, { notify: true, load: { msg: 'Saving changes...' }, callback: onComplete, manual: true })
    
    const [
        { loading: stLoading, error: stError, fetch },
        { list: statement = [] },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('statementBySubject', { string: '', subject: sid }
        , { notify: true, manual: false }, 'entries', 1)

    const [unusedCredits, setUnusedCredits] = useState(0)
    usePet('creditNotes', { supplier: sid, limit: 500 }, { notify: false, callback: ({ data }) => {
        setUnusedCredits((data?.creditNotes ?? []).reduce((total, note) => total + (note.amount - note.amountApplied), 0))
    } })

    const editDetails = (fld, vl) => {
        if (supplier[fld] !== vl) {
            setVals()
            setField(fld)
            setVal(vl)
            editSupplier()
        }
    }

    const editArray = (fld, vls) => {
        if (fld === 'phones') {
            setPhones(vls)
        } else {
            setEmails(vls)
        }

        setVals(vls)
        setVal()
        setField(fld)
        editSupplier()
    }

    const inAccounts = path.includes('accounts')

    const authorized = (user?.issuperuser || user.stations?.some(st => (st.name === 'procurement' || st.name === 'account' || st.name === 'admin') && (st.permissions.includes('edit-supplier') || st.permissions.includes('*'))))

    const edit = fld => authorized ? { onChange: val => editDetails(fld, val) } : null

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            // footer={
            //     // <div className="w-full text-right">
            //     //     <button disabled={ loading } onClick={execute} className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Create Company</button>
            //     // </div>
            // }
            width="70%">
            <Receive
                progress
                loading={loading}
                error={error}>
                {
                    supplier ?
                        <>
                            <div className="flex items-center px-2 py-3">
                                <div className="h-28 flex items-center rounded-full justify-center overflow-hidden">
                                    <img className="h-24 w-24 mx-auto" src={images.company_alt} />
                                </div>
                                <div className="w-9/12 flex items-center">
                                    <div className="w-10/12 flex flex-col leading-none pl-4">
                                        <p className="text-2xl font-semibold mb-0">{supplier?.name}</p>
                                        <p className="text-sm mb-0 font-semibold text-gray-700">{ supplier?.phones?.join(', ') } - { supplier?.emails?.join(', ') }</p>
                                        <p className="text-sm mb-0 font-light text-gray-700">
                                            Added on {stringifyDate(supplier?.date)}
                                        </p>
                                    </div>
                                    <div className="w-2/12">
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    { inAccounts && <Menu.Item onClick={togSelect}>{ select ? 'Deselect All' : 'Select' }</Menu.Item> }
                                                    <Menu.Item>Add Item</Menu.Item>
                                                    <Link to={ `${path}/${sid}/invoices/new`  }>
                                                        <Menu.Item>New Invoice</Menu.Item>
                                                    </Link>             
                                                    <Menu.Item>Add Payment Details</Menu.Item>
                                                    <Menu.Item>New Purchase Order</Menu.Item>
                                                    {
                                                        inAccounts &&
                                                        <>
                                                            {
                                                                canPay ?
                                                                    <Link to={`${path}/${sid}/recordPayment`}>
                                                                        <Menu.Item disabled={ !canPay }>Record Payment</Menu.Item>
                                                                    </Link>
                                                                :
                                                                    <Menu.Item disabled={ !canPay }>Record Payment</Menu.Item>
                                                            }
                                                            {
                                                                canCredit ?
                                                                    <Link to={`${path}/${sid}/recordCreditNote`}>
                                                                        <Menu.Item disabled={ !canCredit }>Record Credit Note</Menu.Item>
                                                                    </Link>
                                                                :
                                                                    <Menu.Item disabled={ !canCredit }>Record Credit Note</Menu.Item>
                                                            }
                                                            <Menu.Item>Record Expense</Menu.Item>
                                                        </>
                                                    }
                                                </Menu>
                                            }>
                                            <button className="btn ml-2 py-2.1 hisani-btn-green-light text-white">Actions <AiOutlineDown className="ml-2" /></button>
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>
                            <Tabs defaultActiveKey="0">
                                <TabPane key="0" tab="Overview">
                                    <div className="w-full flex">
                                        <div className="w-1/3 bg-gray-100 py-4 px-5">
                                            <h2 className="text-base font-bold text-gray-700">General Information</h2>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Name
                                                </label>
                                                <Paragraph editable={edit('name')}>{supplier?.name ?? ''}</Paragraph>
                                            </div>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Display Name
                                                </label>
                                                <Paragraph editable={edit('displayName')}>{supplier?.displayName ?? ''}</Paragraph>
                                            </div>
                                            <h2 className="text-base font-bold text-gray-700">Contact Information</h2>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Emails
                                                </label>
                                                {
                                                    authorized ?
                                                        <Creatable
                                                            isMulti
                                                            onBlur={editSupplier}
                                                            onCreateOption={x => {
                                                                let temp = [...phones, { label: x, value: x }]
                                                                editArray('emails', temp)
                                                            }}
                                                            className="min-w-56 mr-4"
                                                            value={emails}
                                                            onChange={vls => editArray('emails', vls)}
                                                            options={emails}
                                                            placeholder="Edit Email Addresses" />
                                                        : <p>{(supplier?.emails ?? []).join(', ')}</p>
                                                }
                                            </div>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Phone Numbers
                                                </label>
                                                {
                                                    authorized ?
                                                        <Creatable
                                                            isMulti
                                                            onBlur={editSupplier}
                                                            onCreateOption={x => {
                                                                let temp = [...phones, { label: x, value: x }]
                                                                editArray('phones', temp)
                                                            }}
                                                            className="min-w-56 mr-4"
                                                            value={phones}
                                                            onChange={vls => editArray('phones', vls)}
                                                            options={phones}
                                                            placeholder="Edit Phone Numbers" />
                                                        : <p>{(supplier?.phones ?? []).join(', ')}</p>
                                                }
                                            </div>
                                            <h2 className="text-base font-bold text-gray-700">Contact Person</h2>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Name
                                                </label>
                                                <Paragraph editable={edit('contactName')}>{supplier?.contactPerson?.name ?? ''}</Paragraph>
                                            </div>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Phone Number
                                                </label>
                                                <Paragraph editable={edit('contactPhone')}>{supplier?.contactPerson?.phone ?? ''}</Paragraph>
                                            </div>
                                            <div className="w-full ml-1 md:mb-0">
                                                <label className="block tracking-wide text-gray-500 font-semibold mb-2" htmlFor="grid-name">
                                                    Email Address
                                                </label>
                                                <Paragraph editable={edit('contactEmail')}>{supplier?.contactPerson?.email ?? ''}</Paragraph>
                                            </div>
                                        </div>
                                        <div className="w-2/3 bg-white py-4 px-5">
                                            <h2 className="text-xl font-bold text-gray-700">Payables</h2>
                                            <table className="table-auto w-full">
                                                {/* Table header */}
                                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                    <tr>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-center">Outstanding Payables</div>
                                                        </th>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-center">Unused Credits</div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                {/* Table body */}
                                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                    <tr>
                                                        <td className="p-2">
                                                            <div className="text-center">Ksh. { -1 * statement.reduce((total, stat) => total + stat.newBalance, 0) }</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="text-center">Ksh. { unusedCredits }</div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    { sid }
                                </TabPane>
                                <TabPane key="1" tab="Payment Details">
                                    {
                                        supplier?.paymentDetails?.length > 0 ?
                                            <table className="table-auto w-full">
                                                {/* Table header */}
                                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                    <tr>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-center">No</div>
                                                        </th>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-left">Type</div>
                                                        </th>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-center">Details</div>
                                                        </th>
                                                        <th className="p-2">
                                                            <div className="font-semibold text-center">Date</div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                {/* Table body */}
                                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                    {
                                                        supplier?.paymentDetails?.map((details, key) =>
                                                            <tr>
                                                                <td className="p-2">
                                                                    <div className="text-center">{((page - 1) * limit) + key + 1}.</div>
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="">{details?.type}</div>
                                                                </td>
                                                                <td className="p-2">
                                                                    {/* <div className="text-center">{ details?.}</div> */}
                                                                </td>
                                                                <td className="p-2">
                                                                    <div className="text-center">{stringifyDate(details.date)}</div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                            :
                                            <div className="w-full h-16 flex items-center justify-center">
                                                <p className="font-semibold text-base">Please add payment details by clicking above</p>
                                            </div>
                                    }
                                </TabPane>
                                <TabPane key="2" tab="Items">
                                </TabPane>
                                <TabPane key="3" tab="Invoices">
                                    <Invoices 
                                        id={sid} 
                                        path={ path }
                                        select={ select } 
                                        supplier={ supplier } 
                                        selected={ invoices } 
                                        setInvoices={ setInvoices } 
                                        onComplete={ () => {
                                            fetch()
                                            toggleSelect()
                                            onComplete()
                                        }} />
                                </TabPane>
                                <TabPane key="4" tab="Purchase Orders">
                                    <PurchaseOrders id={sid} path={ path } />
                                </TabPane>
                                <TabPane key="5" tab="Transactions">
                                    <Transactions id={ sid } path={ path } />
                                </TabPane>
                                <TabPane key="6" tab="Statement">
                                    <Statement id={ sid } path={ path } />
                                </TabPane>
                                <TabPane key="7" tab="Credit Notes">
                                    <CreditNotes id={ sid } path={ path } />
                                </TabPane>
                            </Tabs>
                        </>
                        : null
                }
            </Receive>
        </Drawer>
    )
}

export default Supplier